import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ContentLayout,
  CopyToClipboard,
  ExpandableSection,
  FormField,
  Header,
  Icon,
  Link,
  Modal,
  PromptInput,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from "@amzn/awsui-components-react";
import { PromptInputProps } from "@amzn/awsui-components-react/polaris/prompt-input";
import useChatUat, {
  ChatUatAction,
  ChatUatEntry,
  ChatUatFailure,
} from "../../hooks/useChatUat";
import { getCurrentMidwaySubject } from "../../auth/MidwayJwtToken";
import {
  ChatUatAudience,
  ChatUatContextReference,
  ChatUatFeedbackRequestRatingEnum,
  ChatUatFeedbackRequestReasonEnum,
  ProjectValidationCase,
} from "../../lusca-search-api/generated-src";
import { ChatUatApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import "../chat/chat.scss";
import ChatUatMarkdown from "./ChatUatMarkdown";
import {
  buildChatUatReplySuggestions,
  buildChatUatStarterPrompts,
  CHAT_UAT_AUDIENCE_OPTIONS,
  readChatUatAudience,
  storeChatUatAudience,
} from "./chatUatPersonalization";
import "./chatUat.scss";

const WIKI_URL = "https://w.amazon.com/bin/view/AIV/FinSys/TRACK/Systems/Lusca";

const MAX_PROMPT_CHARACTERS = 4000;
const ADD_REVIEW_TO_ASANA_PROMPT =
  "Add the current review to the linked Asana task.";
const FEEDBACK_EMAIL = "olivergk@amazon.com";
const MAX_FEEDBACK_EMAIL_BODY_CHARS = 1500;

export function chatUatFeedbackEmailHref(
  response: string,
  reason: ChatUatFeedbackRequestReasonEnum,
  comment: string
): string {
  const prefix = [
    `Reason: ${reason}`,
    ...(comment.trim() ? [`Comment: ${comment.trim()}`] : []),
    "",
    "UAT Insights response:",
  ].join("\n");
  const responseLimit = Math.max(
    0,
    MAX_FEEDBACK_EMAIL_BODY_CHARS - prefix.length - 1
  );
  const boundedResponse =
    response.length > responseLimit
      ? `${response.slice(0, Math.max(0, responseLimit - 12))}…(truncated)`
      : response;
  const body = `${prefix}\n${boundedResponse}`;
  return `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(
    "UAT Insights feedback"
  )}&body=${encodeURIComponent(body)}`;
}

interface ChatUatContextDetails {
  reference: ChatUatContextReference;
  testCase: ProjectValidationCase;
}

export interface ChatUatPageHandle {
  startNewConversation: () => void;
}

interface ChatUatPageProps {
  embedded?: boolean;
}

function reviewOutcome(testCase?: ProjectValidationCase): {
  type: "success" | "warning" | "error";
  label: string;
} {
  if (!testCase) {
    return { type: "warning", label: "Review generated" };
  }
  if (testCase.assessment === "wrong_order") {
    return { type: "error", label: "Wrong order" };
  }
  if (testCase.assessment === "accounting_mismatch") {
    return { type: "error", label: "Accounting mismatch" };
  }
  if (testCase.assessment === "cannot_verify") {
    return { type: "warning", label: "Cannot verify" };
  }
  if (testCase.assessment === "incomplete_scope") {
    return { type: "warning", label: "Incomplete scope" };
  }
  return { type: "success", label: "Matches intended test" };
}

function contextReference(
  runId: string | null,
  taskGid: string | null,
  orderId: string | null
): ChatUatContextReference | undefined {
  if (
    !runId ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
      runId
    ) ||
    !taskGid ||
    !/^[0-9]{1,32}$/.test(taskGid) ||
    (orderId !== null && (!orderId || orderId.length > 128))
  ) {
    return undefined;
  }
  return {
    runId,
    taskGid,
    ...(orderId === null ? {} : { orderId }),
  };
}

function ActionOutcome({
  action,
  content,
}: {
  action: ChatUatAction;
  content: string;
}) {
  const isConfirmed = action.outcome === "confirmed";
  const isUncertain = action.outcome === "uncertain";
  let indicatorType: "success" | "warning" | "error" = "error";
  if (isConfirmed) {
    indicatorType = "success";
  } else if (isUncertain) {
    indicatorType = "warning";
  }
  return (
    <div
      className="chat-uat__action-outcome"
      role={isConfirmed || isUncertain ? "status" : "alert"}
      data-testid="chat-uat-action-outcome"
    >
      <SpaceBetween size="xs">
        <StatusIndicator type={indicatorType}>{action.summary}</StatusIndicator>
        {action.reference && (
          <Box fontSize="body-s">
            {action.reference.label}{" "}
            <span
              className="chat-uat__action-reference"
              data-testid="chat-uat-action-reference"
            >
              <CopyToClipboard
                variant="inline"
                textToCopy={action.reference.value}
                copyButtonAriaLabel={`Copy ${action.reference.label}`}
                copySuccessText={`${action.reference.label} copied`}
                copyErrorText={`${action.reference.label} failed to copy`}
              />
            </span>
          </Box>
        )}
        <Box
          fontSize="body-s"
          color={isConfirmed ? "text-body-secondary" : undefined}
        >
          <ChatUatMarkdown text={content} />
        </Box>
      </SpaceBetween>
    </div>
  );
}

function FailureOutcome({
  failure,
  content,
}: {
  failure: ChatUatFailure;
  content: string;
}) {
  const occurredAt = new Date(failure.occurredAt);
  return (
    <div
      className="chat-uat__failure-outcome"
      data-testid="chat-uat-failure-outcome"
    >
      <SpaceBetween size="xs">
        <StatusIndicator type="error">Request not completed</StatusIndicator>
        <ChatUatMarkdown text={content} />
        <Box fontSize="body-s">
          <strong>Support reference</strong>{" "}
          <span data-testid="chat-uat-support-reference">
            <CopyToClipboard
              variant="inline"
              textToCopy={failure.jobId}
              copyButtonAriaLabel="Copy support reference"
              copySuccessText="Support reference copied"
              copyErrorText="Support reference failed to copy"
            />
          </span>
        </Box>
        <Box fontSize="body-s">
          <strong>Failed at</strong>{" "}
          <time dateTime={occurredAt.toISOString()}>
            {occurredAt.toLocaleString()}
          </time>
        </Box>
        {failure.failureCode === "review_too_large" && (
          <Box fontSize="body-s">
            Share the support reference with TRACK if support is needed.
          </Box>
        )}
      </SpaceBetween>
    </div>
  );
}

function MessageRow({
  entry,
  onPositiveFeedback,
  onNegativeFeedback,
  feedbackSubmitting,
}: {
  entry: ChatUatEntry;
  onPositiveFeedback: (entryId: string) => void;
  onNegativeFeedback: (entryId: string) => void;
  feedbackSubmitting: boolean;
}) {
  const isUser = entry.role === "user";
  const bubbleClass = entry.isError
    ? "chat-uat__bubble chat-uat__bubble--assistant chat-uat__bubble--error"
    : `chat-uat__bubble chat-uat__bubble--${entry.role}`;
  let content: React.ReactNode = <ChatUatMarkdown text={entry.content} />;
  if (isUser) {
    content = entry.content;
  } else if (entry.failure) {
    content = (
      <FailureOutcome failure={entry.failure} content={entry.content} />
    );
  } else if (entry.action) {
    content = <ActionOutcome action={entry.action} content={entry.content} />;
  } else if (entry.isPassingReview) {
    content = (
      <div
        className="chat-uat__action-outcome"
        role="status"
        data-testid="chat-uat-passing-review"
      >
        <SpaceBetween size="xs">
          <StatusIndicator type="success">Shadow test passed</StatusIndicator>
          <ChatUatMarkdown text={entry.content} />
        </SpaceBetween>
      </div>
    );
  }

  return (
    <div
      className={`chat-uat__row ${isUser ? "chat-uat__row--user" : ""}`}
      role="group"
      aria-label={isUser ? "Your message" : "UAT Insights message"}
    >
      <div
        className={`chat-uat__avatar chat-uat__avatar--${entry.role}`}
        aria-hidden="true"
      >
        <Icon name={isUser ? "user-profile" : "gen-ai"} />
      </div>
      <div className="chat-uat__message">
        <div
          className={bubbleClass}
          data-testid={`chat-uat-bubble-${entry.role}`}
          role={entry.isError ? "alert" : undefined}
        >
          {content}
        </div>
        {!isUser && entry.groundedIn && entry.groundedIn.length > 0 && (
          <div className="chat-uat__grounding">
            <span>Grounded in</span>
            {entry.groundedIn.map((source) => (
              <span key={source} className="chat-uat__ground-chip">
                {source}
              </span>
            ))}
          </div>
        )}
        {!isUser && entry.feedbackTarget && !entry.isError && (
          <div className="chat-uat__feedback">
            <span>Was this useful?</span>
            <Button
              variant="icon"
              iconName={
                entry.feedbackRating === ChatUatFeedbackRequestRatingEnum.Up
                  ? "thumbs-up-filled"
                  : "thumbs-up"
              }
              ariaLabel="Rate this answer useful"
              disabled={feedbackSubmitting}
              onClick={() => onPositiveFeedback(entry.id)}
            />
            <Button
              variant="icon"
              iconName={
                entry.feedbackRating === ChatUatFeedbackRequestRatingEnum.Down
                  ? "thumbs-down-filled"
                  : "thumbs-down"
              }
              ariaLabel="Rate this answer not useful"
              disabled={feedbackSubmitting}
              onClick={() => onNegativeFeedback(entry.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingRow() {
  return (
    <div className="chat-uat__row">
      <div
        className="chat-uat__avatar chat-uat__avatar--assistant"
        aria-hidden="true"
      >
        <Icon name="gen-ai" />
      </div>
      <div className="chat-uat__bubble chat-uat__bubble--assistant">
        <span
          className="chat-uat__typing"
          role="status"
          aria-label="UAT Insights is thinking"
        >
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  );
}

const ChatUatPage = forwardRef<ChatUatPageHandle, ChatUatPageProps>(
  function ChatUatPage({ embedded = false }, ref) {
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedRunId = searchParams.get("runId");
    const requestedTaskGid = searchParams.get("taskGid");
    const requestedOrderId = searchParams.get("orderId");
    const contextRequested = Boolean(
      requestedRunId || requestedTaskGid || requestedOrderId
    );
    const requestedContext = useMemo(
      () =>
        contextReference(requestedRunId, requestedTaskGid, requestedOrderId),
      [requestedOrderId, requestedRunId, requestedTaskGid]
    );
    const chatUatApi = useMemo(() => ChatUatApiFactory(), []);
    const contextQuery = useQuery<ChatUatContextDetails, Error>(
      [
        "chat-uat-context",
        requestedContext?.runId,
        requestedContext?.taskGid,
        requestedContext?.orderId,
      ],
      async () => {
        if (!requestedContext) {
          throw new Error("Invalid ChatUAT context");
        }
        const runResponse = await chatUatApi.getChatUatScopeValidationRun(
          requestedContext.runId
        );
        const testCase = runResponse.data.cases.find(
          (candidate) => candidate.taskGid === requestedContext.taskGid
        );
        if (
          !testCase ||
          (requestedContext.orderId !== undefined &&
            requestedContext.orderId !== null &&
            testCase.orderId !== requestedContext.orderId)
        ) {
          throw new Error("Validation case not found");
        }
        return {
          reference: {
            runId: requestedContext.runId,
            taskGid: requestedContext.taskGid,
            ...(testCase.orderId ? { orderId: testCase.orderId } : {}),
          },
          testCase,
        };
      },
      {
        enabled: Boolean(requestedContext),
        retry: false,
      }
    );
    const actorId = getCurrentMidwaySubject();
    const [audience, setAudience] = useState<ChatUatAudience>(() =>
      readChatUatAudience(actorId)
    );
    const {
      entries,
      artefact,
      sendMessage,
      submitFeedback,
      markFeedbackRating,
      reset,
      isLoading,
    } = useChatUat(requestedContext, audience);
    const [input, setInput] = useState("");
    const [focusRequest, setFocusRequest] = useState(0);
    const [feedbackEntryId, setFeedbackEntryId] = useState<string>();
    const [feedbackReason, setFeedbackReason] =
      useState<ChatUatFeedbackRequestReasonEnum>();
    const [feedbackComment, setFeedbackComment] = useState("");
    const [feedbackSubmittingId, setFeedbackSubmittingId] = useState<string>();
    const [feedbackError, setFeedbackError] = useState<string>();
    const streamRef = useRef<HTMLDivElement>(null);
    const promptRef = useRef<PromptInputProps.Ref>(null);
    const previousLoading = useRef(isLoading);

    useEffect(() => {
      setAudience(readChatUatAudience(actorId));
    }, [actorId]);

    useEffect(() => {
      const justSettled = previousLoading.current && !isLoading;
      if (!isLoading && (justSettled || focusRequest > 0)) {
        promptRef.current?.focus();
      }
      previousLoading.current = isLoading;
    }, [focusRequest, isLoading]);

    useEffect(() => {
      const el = streamRef.current;
      if (el && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight });
      }
    }, [entries, isLoading]);

    const promptTooLong = Array.from(input).length > MAX_PROMPT_CHARACTERS;
    const contextBlocked =
      contextRequested &&
      (!requestedContext || contextQuery.isLoading || !contextQuery.data);
    const submissionBlocked = isLoading || contextBlocked;

    const submit = () => {
      if (submissionBlocked || promptTooLong || !input.trim()) {
        return;
      }
      sendMessage(input, { onSubmitted: () => setInput("") });
    };

    const startNewConversation = useCallback(() => {
      reset();
      setInput("");
      setFocusRequest((request) => request + 1);
    }, [reset]);

    useImperativeHandle(
      ref,
      () => ({
        startNewConversation,
      }),
      [startNewConversation]
    );

    const clearContext = () => {
      reset();
      const next = new URLSearchParams(searchParams);
      ["runId", "taskGid", "orderId"].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    };

    const sendPositiveFeedback = async (entryId: string) => {
      setFeedbackError(undefined);
      setFeedbackSubmittingId(entryId);
      try {
        await submitFeedback({
          entryId,
          rating: ChatUatFeedbackRequestRatingEnum.Up,
        });
      } catch (_error) {
        setFeedbackError(
          "Feedback could not be submitted. The answer is unchanged."
        );
      } finally {
        setFeedbackSubmittingId(undefined);
      }
    };

    const openNegativeFeedback = (entryId: string) => {
      setFeedbackEntryId(entryId);
      setFeedbackReason(undefined);
      setFeedbackComment("");
      setFeedbackError(undefined);
    };

    const closeNegativeFeedback = () => {
      setFeedbackEntryId(undefined);
      setFeedbackReason(undefined);
      setFeedbackComment("");
    };

    const emailNegativeFeedback = () => {
      if (feedbackEntryId) {
        markFeedbackRating(
          feedbackEntryId,
          ChatUatFeedbackRequestRatingEnum.Down
        );
      }
      closeNegativeFeedback();
    };

    const feedbackEntry = entries.find((entry) => entry.id === feedbackEntryId);
    const feedbackEmailHref =
      feedbackEntry && feedbackReason
        ? chatUatFeedbackEmailHref(
            feedbackEntry.content,
            feedbackReason,
            feedbackComment
          )
        : undefined;
    const currentReviewOutcome = reviewOutcome(contextQuery.data?.testCase);
    const starterPrompts = useMemo(
      () => buildChatUatStarterPrompts(audience, contextQuery.data?.testCase),
      [audience, contextQuery.data?.testCase]
    );
    const latestAssistant = useMemo(
      () => [...entries].reverse().find((entry) => entry.role === "assistant"),
      [entries]
    );
    const replySuggestions = useMemo(
      () =>
        buildChatUatReplySuggestions({
          audience,
          testCase: contextQuery.data?.testCase,
          hasArtefact: Boolean(artefact),
          latestAssistant,
          askedPrompts: entries
            .filter((entry) => entry.role === "user")
            .map((entry) => entry.content),
        }),
      [
        artefact,
        audience,
        contextQuery.data?.testCase,
        entries,
        latestAssistant,
      ]
    );
    const selectedAudienceOption =
      CHAT_UAT_AUDIENCE_OPTIONS.find((option) => option.value === audience) ??
      CHAT_UAT_AUDIENCE_OPTIONS[0];
    const changeAudience = (nextAudience: ChatUatAudience) => {
      setAudience(nextAudience);
      storeChatUatAudience(actorId, nextAudience);
    };
    const workspace = (
      <SpaceBetween size="m">
        {contextRequested && !requestedContext && (
          <Alert
            type="error"
            header="Invalid validation context"
            action={<Button onClick={clearContext}>Clear</Button>}
          >
            Select a validation result again.
          </Alert>
        )}
        {requestedContext && contextQuery.isLoading && (
          <StatusIndicator type="loading">
            Loading validation context
          </StatusIndicator>
        )}
        {requestedContext && contextQuery.isError && (
          <Alert
            type="error"
            header="Validation context unavailable"
            action={<Button onClick={clearContext}>Clear</Button>}
          >
            Select the validation result again.
          </Alert>
        )}
        {feedbackError && !feedbackEntryId && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setFeedbackError(undefined)}
          >
            {feedbackError}
          </Alert>
        )}
        <section
          className="chat-uat__workspace"
          aria-labelledby="uat-chat-title"
        >
          <header className="chat-uat__workspace-header">
            <div className="chat-uat__workspace-heading">
              <div className="chat-uat__workspace-icon" aria-hidden="true">
                <Icon name="gen-ai" />
              </div>
              <div>
                <h2 id="uat-chat-title">Investigation Chat</h2>
                <p>
                  {contextQuery.data
                    ? "Grounded in the selected Asana test case and its Raven result"
                    : "Grounded in Raven shadow-test data and available UAT evidence"}
                </p>
              </div>
            </div>
            <div className="chat-uat__workspace-controls">
              <div className="chat-uat__audience">
                <FormField label="Answer for">
                  <Select
                    ariaLabel="Answer for"
                    selectedOption={selectedAudienceOption}
                    onChange={({ detail }) => {
                      const { value } = detail.selectedOption;
                      if (value === "customer" || value === "engineer") {
                        changeAudience(value);
                      }
                    }}
                    options={CHAT_UAT_AUDIENCE_OPTIONS}
                  />
                </FormField>
              </div>
              {contextQuery.data && (
                <Link href={contextQuery.data.testCase.taskUrl} external>
                  <span
                    className="chat-uat__selected-case"
                    data-testid="chat-uat-context"
                  >
                    {contextQuery.data.testCase.taskName}
                  </span>
                </Link>
              )}
            </div>
          </header>
          <div
            className={`chat-uat__workspace-layout ${
              artefact ? "chat-uat__workspace-layout--with-review" : ""
            }`}
          >
            <div
              className="chat-uat__conversation-panel"
              data-testid="chat-uat-conversation-panel"
            >
              <div className="chat-uat__workspace-body">
                <div
                  className="chat-uat__thread"
                  ref={streamRef}
                  role="log"
                  aria-label="UAT Insights conversation"
                  aria-live="polite"
                  aria-relevant="additions"
                  aria-busy={isLoading}
                >
                  {entries.length === 0 && !isLoading && (
                    <div className="chat-uat__empty">
                      <SpaceBetween size="s">
                        <Box color="text-body-secondary">
                          Select a validation result or ask about a shadow-test
                          order.
                        </Box>
                        <div className="chat-uat__starters">
                          {starterPrompts.map(({ label, prompt }) => (
                            <Button
                              key={label}
                              onClick={() => sendMessage(prompt)}
                              disabled={submissionBlocked}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                      </SpaceBetween>
                    </div>
                  )}
                  {entries.map((entry) => (
                    <MessageRow
                      key={entry.id}
                      entry={entry}
                      onPositiveFeedback={(entryId) => {
                        sendPositiveFeedback(entryId).catch(() => undefined);
                      }}
                      onNegativeFeedback={openNegativeFeedback}
                      feedbackSubmitting={feedbackSubmittingId === entry.id}
                    />
                  ))}
                  {isLoading && <ThinkingRow />}
                </div>
                {!isLoading && replySuggestions.length > 0 && (
                  <div
                    className="chat-uat__followups"
                    data-testid="chat-uat-followups"
                  >
                    <div className="chat-uat__followups-label">
                      <Icon name="gen-ai" /> Suggested follow-ups
                    </div>
                    <div className="chat-uat__followup-actions">
                      {replySuggestions.map(({ label, prompt }) => (
                        <Button
                          key={prompt}
                          onClick={() => sendMessage(prompt)}
                          disabled={submissionBlocked}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <footer className="chat-uat__workspace-footer">
                <FormField
                  stretch
                  errorText={
                    promptTooLong
                      ? "Questions are limited to 4,000 characters."
                      : undefined
                  }
                >
                  <PromptInput
                    ref={promptRef}
                    value={input}
                    onChange={({ detail }) => setInput(detail.value)}
                    onAction={submit}
                    ariaLabel={
                      contextQuery.data
                        ? "Ask about the selected test case"
                        : "Ask about a shadow-test order"
                    }
                    disabled={submissionBlocked}
                    disableActionButton={
                      submissionBlocked || !input.trim() || promptTooLong
                    }
                    disableBrowserAutocorrect
                    spellcheck={false}
                    actionButtonIconName="arrow-up"
                    actionButtonAriaLabel="Send message"
                    placeholder={
                      contextQuery.data
                        ? "Ask about the selected test case"
                        : "Ask about a shadow-test order"
                    }
                    maxRows={4}
                    data-testid="chat-uat-input"
                  />
                </FormField>
              </footer>
            </div>
            {artefact && (
              <aside
                className="chat-uat__post-thread"
                data-testid="chat-uat-post-thread"
                aria-label="UAT review"
              >
                <div
                  className="chat-uat__review"
                  data-testid="chat-uat-artefact"
                >
                  <div className="chat-uat__review-title">
                    <strong>UAT review</strong>
                    <StatusIndicator type={currentReviewOutcome.type}>
                      {currentReviewOutcome.label}
                    </StatusIndicator>
                  </div>
                  <div className="chat-uat__artefact">
                    <ChatUatMarkdown text={artefact.primary} />
                  </div>
                  {artefact.details && (
                    <ExpandableSection
                      variant="footer"
                      headerText="Coverage and provenance"
                    >
                      <div className="chat-uat__artefact">
                        <ChatUatMarkdown text={artefact.details} />
                      </div>
                    </ExpandableSection>
                  )}
                  <div className="chat-uat__review-actions">
                    <Button
                      iconName="contact"
                      disabled={!contextQuery.data || submissionBlocked}
                      onClick={() =>
                        sendMessage(ADD_REVIEW_TO_ASANA_PROMPT, {
                          preserveArtefact: true,
                        })
                      }
                    >
                      Add review to Asana
                    </Button>
                    <CopyToClipboard
                      copyButtonText="Copy review"
                      copyButtonAriaLabel="Copy the review for sign-off"
                      copySuccessText="Review copied"
                      copyErrorText="Review failed to copy"
                      textToCopy={artefact.raw}
                    />
                  </div>
                </div>
              </aside>
            )}
          </div>
        </section>
      </SpaceBetween>
    );

    const feedbackModal = feedbackEntryId ? (
      <Modal
        visible
        onDismiss={closeNegativeFeedback}
        header="What could be better?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={closeNegativeFeedback}>Cancel</Button>
              <Button
                variant="primary"
                href={feedbackEmailHref}
                onClick={emailNegativeFeedback}
                disabled={!feedbackReason}
              >
                Email feedback
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {feedbackError && <Alert type="error">{feedbackError}</Alert>}
          <FormField label="Reason">
            <Select
              selectedOption={
                feedbackReason
                  ? {
                      label: {
                        [ChatUatFeedbackRequestReasonEnum.Incorrect]:
                          "Incorrect",
                        [ChatUatFeedbackRequestReasonEnum.MissingEvidence]:
                          "Missing evidence",
                        [ChatUatFeedbackRequestReasonEnum.Unclear]: "Unclear",
                        [ChatUatFeedbackRequestReasonEnum.TooSlow]: "Too slow",
                        [ChatUatFeedbackRequestReasonEnum.Other]: "Other",
                      }[feedbackReason],
                      value: feedbackReason,
                    }
                  : null
              }
              onChange={({ detail }) =>
                setFeedbackReason(
                  detail.selectedOption
                    .value as ChatUatFeedbackRequestReasonEnum
                )
              }
              options={[
                {
                  label: "Incorrect",
                  value: ChatUatFeedbackRequestReasonEnum.Incorrect,
                },
                {
                  label: "Missing evidence",
                  value: ChatUatFeedbackRequestReasonEnum.MissingEvidence,
                },
                {
                  label: "Unclear",
                  value: ChatUatFeedbackRequestReasonEnum.Unclear,
                },
                {
                  label: "Too slow",
                  value: ChatUatFeedbackRequestReasonEnum.TooSlow,
                },
                {
                  label: "Other",
                  value: ChatUatFeedbackRequestReasonEnum.Other,
                },
              ]}
              placeholder="Choose a reason"
            />
          </FormField>
          <FormField
            label="Comment"
            description="Optional"
            constraintText={`${feedbackComment.length}/1000`}
          >
            <Textarea
              value={feedbackComment}
              onChange={({ detail }) =>
                setFeedbackComment(detail.value.slice(0, 1000))
              }
              rows={4}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    ) : null;

    if (embedded) {
      return (
        <>
          {workspace}
          {feedbackModal}
        </>
      );
    }

    return (
      <ContentLayout
        defaultPadding
        header={
          <Header
            variant="h1"
            info={
              <Link href={WIKI_URL} variant="info">
                Info
              </Link>
            }
            description="Ask about a shadow-test order and get a review you can circulate for sign-off."
            actions={
              <Button onClick={startNewConversation}>New conversation</Button>
            }
          >
            TrackStars UAT Insights
          </Header>
        }
      >
        {workspace}
        {feedbackModal}
      </ContentLayout>
    );
  }
);

export default ChatUatPage;
